import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import {
  stableSort,
  getComparator,
} from "../../../../../common-utilities/sortingUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmation from "../../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { DeleteTestCategory } from "../../../../services/TestLibService";
import ViewTestCategory from "./ViewTestCategory";
import CommonLoader from "../../../common/CommonLoader";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GetTestCategories } from "../../../../services/TestLibService";
import AddTestCategory from "./AddTestCategory"; // Import the AddTestCategory component

const TestCategoriesTable = () => {
  const columns = ["Test Category", "GL Code", "Actions"];
  const columnHeaders = ["testCategory", "glCode"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [categoriesData, setCategoriesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await GetTestCategories();
        setCategoriesData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await GetTestCategories();
        setCategoriesData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [isDeleted]);

  useEffect(() => {
    if (!categoriesData) {
      return;
    }

    const filteredResults = categoriesData.filter((category) =>
      columnHeaders.some((header) =>
        category[header]?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, categoriesData]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (category) => {
    setSelectedCategory(category);
  };

  const handleBackClick = () => {
    setSelectedCategory(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category.testCategory);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedCategory(null)
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteTestCategory(selectedCategory);
      if (response.results === true) {
        NotificationManager.success("Test category deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setIsDeleted(!isDeleted);
        setSelectedCategory(null)
      } else {
        NotificationManager.error("Error deleting test category");
      }
    } catch (error) {
      NotificationManager.error("Error deleting test category");
    }
  };

  const handleAddCategoryClick = () => {
    setSelectedCategory(null);
    setIsAddCategoryDialogOpen(true);
  };

  const handleEditCategoryClick = (category) => {
    setSelectedCategory(category);
    setIsAddCategoryDialogOpen(true);
  };

  const handleAddCategoryDialogClose = () => {
    setIsAddCategoryDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleAddCategoryDialogSucess = () => {
    setIsAddCategoryDialogOpen(false);
    setSelectedCategory(null);
    setIsDeleted(!isDeleted)
  };

  const renderActions = (category) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditCategoryClick(category)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(category)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(category)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
    <span className="title">Test Category Management </span>
    <hr />
      {selectedCategory && !isAddCategoryDialogOpen && !isDeleteConfirmationOpen? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewTestCategory category={selectedCategory} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddCategoryClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column, index) => (
                      <TableCell
                        key={column}
                        onClick={() => columnHeaders[index] && handleRequestSort(columnHeaders[index])}
                        sx={{ cursor: columnHeaders[index] ? "pointer" : "default" }}
                      >
                        {columnHeaders[index] ? (
                          <TableSortLabel
                            active={orderBy === columnHeaders[index]}
                            direction={orderBy === columnHeaders[index] ? order : "asc"}
                            sx={{
                              color: "white !important",
                              "& .MuiTableSortLabel-icon": { color: "white !important" },
                            }}
                          >
                            {column}
                          </TableSortLabel>
                        ) : (
                          column
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((category, index) => (
                      <TableRow
                        key={category.testCategory}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>{category[column]}</TableCell>
                        ))}
                        <TableCell>{renderActions(category)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation"
            message="Are you sure you want to delete this category?"
            color="red"
          />
        </>
      )}

      {isAddCategoryDialogOpen && (
        <AddTestCategory
          open={isAddCategoryDialogOpen}
          onClose={handleAddCategoryDialogClose}
          onSave={handleAddCategoryDialogSucess}
          selectedCategory={selectedCategory} 
        />
      )}
    </>
  );
};

export default TestCategoriesTable;
